/**
 * Charted dangers lying in the boat's path.
 *
 * This is the replacement for the always-on magenta isolated-danger symbol. That
 * symbol marked every hazard shallower than the safety depth that sat in
 * otherwise-safe water, everywhere on the chart, at every zoom — which on the
 * Nova Scotia coast is a field of identical circles. A symbol that is always
 * present cannot mean "look at this".
 *
 * So the question changed. Instead of "is this rock dangerous", which the depth
 * shading already answers, this asks **"is this rock in front of me"**: inside a
 * 30° sector along the current course, out to the length of the predicted track.
 * Far fewer marks, each of them about the next few minutes.
 *
 * Two limits, both of which the UI has to state rather than hide:
 *
 * - **It reads rendered tiles**, so it can only see what is loaded for the
 *   current viewport. A cone running off the edge of the screen is not searched
 *   past that edge, and an empty result is never proof of clear water. This is
 *   the same limitation route checking documents, for the same reason.
 * - **It inherits dead reckoning's assumption.** The sector is where the boat
 *   goes if course and speed hold. It is not a plan, it has not been checked
 *   like a route, and nothing here says the water between the marks is safe.
 *
 * Layer filters are the single source of truth for "dangerous to this boat":
 * `queryRenderedFeatures` applies them, so anything coming back from the hazard
 * or clearance danger layers already qualifies. Re-testing the properties here
 * would be a second copy of `dangerousHazardFilter` to keep in step, and the
 * copy that drifted would be the one deciding what the skipper is warned about.
 */

import type { Map as MapLibreMap, MapGeoJSONFeature } from 'maplibre-gl';

import { type BoatProfile, isUnsafeDepth, safetyDepth } from '../core/boat';
import {
  type ForwardCone,
  coneHit,
  conePolygon,
  coneRings,
  coneSlice,
} from '../core/forward';
import { type LatLon, destinationPoint } from '../core/geo';
import { DEPTH_CHECK_SPACING_M } from '../core/route';
import { chartedDepthAt } from './chartDepth';
import {
  type DistanceUnit,
  type DepthUnit,
  formatDepth,
  formatDistance,
} from '../core/units';
import { type ChartManifest, chartSourceId } from './style';

/**
 * Most marks drawn at once.
 *
 * A cone crossing a shoal can hold dozens of charted rocks, and thirty magenta
 * rings is the clutter this feature exists to remove. The nearest are kept —
 * they are the ones you meet first — and the count of what was dropped is
 * returned so the UI can say so instead of quietly showing a dozen.
 */
export const MAX_FORWARD_DANGERS = 12;

/**
 * Largest square a depth ring is queried with, pixels.
 *
 * Zoomed right in, a ring near the far end of a long cone is thousands of pixels
 * across, and `queryRenderedFeatures` costs what it covers. Past this the query
 * stops covering the full width of the sector — but nothing off screen could be
 * answered anyway, and the arc walk that follows a hit is what decides where the
 * shoal is, so a clipped square costs coverage and never correctness.
 */
const MAX_RING_RADIUS_PX = 320;

export interface ForwardDanger {
  /** Stable identity across bands and redraws, so markers do not flicker. */
  key: string;
  position: LatLon;
  /** S-57 kind: rock, wreck, obstruction, bridge, overhead_cable, pylon. */
  kind: string | null;
  name: string | null;
  /** Least charted depth, metres. Null means never surveyed — treat as unsafe. */
  depthM: number | null;
  /** Vertical clearance, metres, for overhead obstructions. */
  clearanceM: number | null;
  /**
   * What kind of danger it is:
   *   `object`    — a charted rock, wreck or obstruction,
   *   `clearance` — something overhead too low for the air draft,
   *   `shallow`   — the edge of water the boat cannot use, which is not an
   *                 object at all and is the commonest way to go aground,
   *   `shoal`     — water the boat can float in but that is notably shallower
   *                 than its surroundings. A caution, not a danger.
   */
  hazard: 'object' | 'clearance' | 'shallow' | 'shoal';
  /**
   * How loudly to say it. `danger` gets the magenta ring and the count in the
   * banner; `caution` gets a quieter mark and is never called a danger — the
   * whole value of the magenta ring is that it does not appear for things you
   * can pass over.
   */
  severity: 'danger' | 'caution';
  distanceM: number;
  /** Signed offset from the course, degrees; negative to port. */
  offsetDeg: number;
  timeToS: number | null;
}

export interface ForwardScan {
  /** Nearest first, capped at `MAX_FORWARD_DANGERS`. Cautions included. */
  dangers: ForwardDanger[];
  /** How many DANGERS were found before the cap — cautions are not dangers. */
  total: number;
}

const EMPTY: ForwardScan = { dangers: [], total: 0 };

const layerIdsFor = (manifest: ChartManifest, suffix: string): string[] =>
  [...manifest.sources]
    .sort((a, b) => b.band - a.band)
    .map((source) => `${chartSourceId(source)}-${suffix}`);

const num = (value: unknown): number | null =>
  (typeof value === 'number' && Number.isFinite(value) ? value : null);

const text = (value: unknown): string | null =>
  (typeof value === 'string' && value.trim() ? value.trim() : null);

/**
 * A representative position for a charted feature.
 *
 * Hazards are points in the canonical schema, but a wreck or obstruction can
 * arrive as an area. The centre of an area is close enough for a warning that is
 * followed by "look at the chart" — and any point of it inside the cone is worth
 * marking, so the centre erring outside the cone is the conservative direction
 * only when the area is large. Areas that large are rare and are shaded anyway.
 */
export function featurePosition(geometry: MapGeoJSONFeature['geometry']): LatLon | null {
  const coordinates: number[][] = [];
  const walk = (value: unknown): void => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      coordinates.push(value as number[]);
      return;
    }
    for (const child of value) walk(child);
  };
  if (!geometry || !('coordinates' in geometry)) return null;
  walk(geometry.coordinates);
  if (coordinates.length === 0) return null;

  let lon = 0;
  let lat = 0;
  for (const [x, y] of coordinates) { lon += x; lat += y; }
  const position = { lat: lat / coordinates.length, lon: lon / coordinates.length };
  return Number.isFinite(position.lat) && Number.isFinite(position.lon) ? position : null;
}

/**
 * Identity for a danger.
 *
 * The same rock is charted in every usage band covering it, and a query
 * straddling a tile boundary returns the same feature more than once, so
 * position alone is not identity and identity alone is not position. The key is
 * both, with the position rounded to about ten metres — enough to absorb the
 * small differences between cells, and far too fine to merge two genuinely
 * different unnamed rocks, which would undercount what is ahead. Undercounting
 * is the one direction this must not err in.
 */
function dangerKey(props: Record<string, unknown>, position: LatLon): string {
  return [
    props.kind ?? '',
    props.objnam ?? '',
    props.valsou ?? '',
    props.watlev ?? '',
    position.lat.toFixed(4),
    position.lon.toFixed(4),
  ].join('|');
}

/**
 * Screen-space box covering the cone.
 *
 * `queryRenderedFeatures` works in pixels, so the sector is projected and its
 * extent taken. The box is a superset of the sector — the corners astern of the
 * boat are in it — which is why every candidate is then tested against
 * `coneHit` rather than trusted because the query returned it.
 */
function conePixelBox(
  map: MapLibreMap,
  cone: ForwardCone,
): [[number, number], [number, number]] | null {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const point of conePolygon(cone)) {
    const { x, y } = map.project([point.lon, point.lat]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
    minY = Math.min(minY, y); maxY = Math.max(maxY, y);
  }
  return [[minX, minY], [maxX, maxY]];
}

/**
 * The first point ahead where the charted depth is less than the boat needs.
 *
 * **This is the half of the problem that charted objects do not cover, and the
 * bigger half.** A shoal is usually not an object in the ENC at all: Ives Knoll
 * in Halifax harbour, half a mile inside this cone, carries no rock, wreck or
 * obstruction feature. It is a depth area whose shallow limit is zero, a
 * scattering of 3 m soundings, and a beacon named after it. Scanning only the
 * hazard layers found nothing there and said so confidently — which is exactly
 * the failure this whole feature exists to avoid.
 *
 * Depth areas are the layer to ask, not soundings: they are always in the style
 * (soundings can be switched off and carry a minzoom), they tile the chart so
 * there is an answer everywhere, and the shallow limit of the area is the
 * conservative reading of what is under it.
 *
 * Probes with no depth area under them are skipped rather than treated as
 * either safe or unsafe: that means the tile is not loaded, and nothing can be
 * concluded from it. The same honesty as §4.4's `probesUnchecked`.
 */
function findShallowWater(
  map: MapLibreMap,
  manifest: ChartManifest,
  boat: BoatProfile,
  cone: ForwardCone,
  /** Depth at or below which to report, metres. Null means the safety depth. */
  thresholdM: number | null,
  severity: ForwardDanger['severity'],
): ForwardDanger | null {
  const depthLayers = layerIdsFor(manifest, 'depth-area').filter((id) => map.getLayer(id));
  if (depthLayers.length === 0) return null;
  if (thresholdM !== null && thresholdM <= 0) return null;

  /**
   * Unsafe means unsafe for THIS boat; shoal means shallower than a depth the
   * skipper nominated. Unknown counts for both — rule 3 does not soften for a
   * caution, and water the chart does not describe is exactly what you want
   * told about.
   */
  const qualifies = (depthM: number | null): boolean => (thresholdM === null
    ? isUnsafeDepth(depthM, boat)
    : depthM === null || depthM <= thresholdM);

  const depthAt = (position: LatLon): number | null | undefined => {
    const point = map.project([position.lon, position.lat]);
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return undefined;
    // One answer per point, from the largest-scale chart covering it. Taking the
    // shallowest across bands instead marks shallow water in sixty feet, because
    // the general chart legitimately lumps 0–18.3 m into one polygon. See
    // chartDepth.ts. `undefined` means no coverage: conclude nothing.
    return chartedDepthAt(map.queryRenderedFeatures(point, { layers: depthLayers }))?.depthM;
  };

  /**
   * Is there anything in this square worth walking?
   *
   * Deliberately weaker than `chartedDepthAt`, and that is the whole point.
   * `chartedDepthAt` answers "how deep is it **here**", and its largest-scale-band
   * rule is right for a point. A ring query is a *square*, so running the same
   * rule over it aggregates an area — and then deep water that band 5 covers at
   * one corner outranks shallow water that only band 4 covers at another, and the
   * shoal is discarded before anything looks at it. That masking was hiding whole
   * shoals from the scan: `tools/verify-alerts.mjs` found the app reporting its
   * nearest danger 1.5 km out with unsafe water 120 m ahead.
   *
   * So the square asks only whether *any* feature in it could qualify, and the
   * per-point walk below — which uses the real rule — decides. False starts cost
   * a walk; a false skip costs the warning.
   */
  const couldQualify = (features: readonly MapGeoJSONFeature[]): boolean =>
    features.some((feature) => qualifies(num((feature.properties ?? {}).drval1)));

  /**
   * Ceiling on point probes for one search.
   *
   * Permissive detection means a square beside a channel triggers a walk for
   * shallow water that is not in the sector at all, and on a coastline that can
   * happen at every ring. The common case stops at the first confirmed hit long
   * before this; the budget only bounds the pathological one, and it is generous
   * enough that reaching it means the sector really is full of near-misses.
   */
  const PROBE_BUDGET = 1200;
  let probes = 0;

  for (const ring of coneRings(cone, DEPTH_CHECK_SPACING_M)) {
    const centre = map.project([ring.position.lon, ring.position.lat]);
    if (!Number.isFinite(centre.x) || !Number.isFinite(centre.y)) continue;

    // One square covering the whole slice of the sector, rather than a point on
    // the course line. Sampling rays missed shoals lying between them, which at
    // the far end of a cone is most of it. `radiusM` covers the arc *and* half
    // the gap to the neighbouring rings, so the slices are contiguous — see
    // coneRings for the gap that cost.
    const edge = destinationPoint(ring.position, cone.courseDeg + 90, ring.radiusM);
    const edgePoint = map.project([edge.lon, edge.lat]);
    const radiusPx = Math.min(
      MAX_RING_RADIUS_PX,
      Math.hypot(edgePoint.x - centre.x, edgePoint.y - centre.y),
    );

    const inRing = map.queryRenderedFeatures(
      [[centre.x - radiusPx, centre.y - radiusPx], [centre.x + radiusPx, centre.y + radiusPx]],
      { layers: depthLayers },
    );
    // Nothing at all means an unloaded tile, not clear water: conclude nothing.
    if (inRing.length === 0 || !couldQualify(inRing)) continue;

    // The square has corners outside the sector, and it is square rather than
    // wedge-shaped, so a hit is a candidate and not a finding. Walk the whole
    // slice — every radius the square covered, not just its middle — to confirm
    // the shoal is really inside the cone and to put the mark where it actually
    // is. Nearest first, and dead ahead first at each range.
    // Half the route-checking spacing. That 50 m is the across-track resolution
    // of the walk, and a shoal narrower than it can be stepped over — the
    // verifier caught exactly that on a 12° beam, where the arc is short and the
    // sample count bottoms out. Walks only happen on a candidate slice and stop
    // at the first confirmed hit, so the finer grid is paid for rarely.
    for (const sample of coneSlice(cone, ring, DEPTH_CHECK_SPACING_M / 2)) {
      if (probes >= PROBE_BUDGET) break;
      probes += 1;
      const depthM = depthAt(sample.position);
      if (depthM === undefined || !qualifies(depthM)) continue;

      const hazard = severity === 'danger' ? 'shallow' : 'shoal';
      return {
        key: `${hazard}@${sample.position.lat.toFixed(4)},${sample.position.lon.toFixed(4)}`,
        position: sample.position,
        kind: hazard === 'shallow' ? 'shallow_water' : 'shoal_water',
        name: null,
        depthM,
        clearanceM: null,
        hazard,
        severity,
        distanceM: sample.distanceM,
        offsetDeg: sample.offsetDeg,
        timeToS: cone.speedMs > 0 ? sample.distanceM / cone.speedMs : null,
      };
    }
    // Hit in the square, nothing inside the sector: a corner, or shallow water
    // just beside the channel. Keep going rather than report water the boat is
    // not heading for.
    if (probes >= PROBE_BUDGET) break;
  }

  return null;
}

/**
 * Charted dangers inside the cone, nearest first.
 *
 * Returns nothing when there is no cone — below about a knot there is no usable
 * course, and marking dangers "ahead" of a boat whose heading is GPS noise would
 * point at a different set of rocks every few seconds.
 */
export function findForwardDangers(
  map: MapLibreMap,
  manifest: ChartManifest,
  boat: BoatProfile,
  cone: ForwardCone | null,
  /** Depth at or below which to add a shoal caution, metres. 0 or null: none. */
  cautionDepthM: number | null = null,
  limit: number = MAX_FORWARD_DANGERS,
): ForwardScan {
  if (!cone) return EMPTY;

  const box = conePixelBox(map, cone);
  if (!box) return EMPTY;

  // Both dangerous-hazard layers: the ones in otherwise-safe water and the ones
  // inside already-shaded shallows. Standing into shallow water is exactly the
  // case where knowing what is in it matters.
  const hazardLayers = [
    ...layerIdsFor(manifest, 'hazard-danger'),
    ...layerIdsFor(manifest, 'hazard-shallow'),
  ].filter((id) => map.getLayer(id));
  // Empty unless the boat has an air draft — `unsafeClearanceFilter` sees to it.
  const clearanceLayers = layerIdsFor(manifest, 'clearance-danger')
    .filter((id) => map.getLayer(id));

  const found = new Map<string, ForwardDanger>();

  const collect = (features: MapGeoJSONFeature[], hazard: ForwardDanger['hazard']): void => {
    for (const feature of features) {
      const position = featurePosition(feature.geometry);
      if (!position) continue;
      const hit = coneHit(cone, position);
      if (!hit) continue;

      const props = (feature.properties ?? {}) as Record<string, unknown>;
      const key = dangerKey(props, position);
      const existing = found.get(key);
      // Same danger from a larger-scale chart wins: better survey, better position.
      if (existing && existing.distanceM <= hit.distanceM) continue;

      found.set(key, {
        key,
        position,
        kind: text(props.kind),
        name: text(props.objnam),
        depthM: num(props.valsou),
        clearanceM: num(props.verclr),
        hazard,
        severity: 'danger',
        distanceM: hit.distanceM,
        offsetDeg: hit.offsetDeg,
        timeToS: hit.timeToS,
      });
    }
  };

  if (hazardLayers.length > 0) {
    collect(map.queryRenderedFeatures(box, { layers: hazardLayers }), 'object');
  }
  if (clearanceLayers.length > 0 && boat.airDraftM !== undefined) {
    collect(map.queryRenderedFeatures(box, { layers: clearanceLayers }), 'clearance');
  }

  const shallow = findShallowWater(map, manifest, boat, cone, null, 'danger');
  if (shallow) found.set(shallow.key, shallow);

  // The shoal caution runs whether or not there is unusable water further on:
  // shoaling from 25 m to 2 m matters at fifteen knots even when 2 m is water
  // the boat can float in. It is only reported when it comes FIRST — behind the
  // point where the water stops being usable it says nothing new.
  if (cautionDepthM !== null && cautionDepthM > 0) {
    const shoal = findShallowWater(map, manifest, boat, cone, cautionDepthM, 'caution');
    if (shoal && (!shallow || shoal.distanceM < shallow.distanceM)) {
      found.set(shoal.key, shoal);
    }
  }

  const dangers = [...found.values()].sort((a, b) => a.distanceM - b.distanceM);
  return {
    dangers: dangers.slice(0, limit),
    // Cautions are not dangers, and the banner must not inflate its count with
    // them. That count is the whole reason the magenta ring still means
    // something.
    total: dangers.filter((danger) => danger.severity === 'danger').length,
  };
}

const KIND_LABELS: Record<string, string> = {
  rock: 'Rock',
  wreck: 'Wreck',
  obstruction: 'Obstruction',
  bridge: 'Bridge',
  overhead_cable: 'Overhead cable',
  pylon: 'Pylon',
  shallow_water: 'Shallow water',
  shoal_water: 'Shoal',
};

/** "Rock", "Wreck", or the honest fallback when the chart does not say. */
export function describeKind(kind: string | null): string {
  return (kind && KIND_LABELS[kind]) || 'Charted danger';
}

/**
 * The one line drawn beside a marker: what it is and how far off.
 *
 * Distance rather than time-to-go, because distance is what the skipper can
 * check against the chart in front of them; time is a function of a speed that
 * is about to change.
 */
export function forwardDangerLabel(danger: ForwardDanger, unit: DistanceUnit): string {
  return `${danger.name ?? describeKind(danger.kind)}  ${formatDistance(danger.distanceM, unit)}`;
}

/** The sentence a panel or banner uses. Says what is unknown, not just what is known. */
export function describeForwardDanger(
  danger: ForwardDanger,
  distanceUnit: DistanceUnit,
  depthUnit: DepthUnit,
  boat?: BoatProfile,
): string {
  const what = danger.name
    ? `${describeKind(danger.kind)} (${danger.name})`
    : describeKind(danger.kind);

  let detail: string;
  if (danger.hazard === 'clearance') {
    detail = danger.clearanceM === null
      ? 'clearance not charted'
      : `${formatDepth(danger.clearanceM, depthUnit)} clearance`;
  } else if (danger.hazard === 'shoal') {
    // Deliberately not phrased against the safety depth: the boat floats here.
    // Saying "less than your safety depth" would be false, and the point of the
    // caution is that it is NOT that.
    detail = danger.depthM === null
      ? 'no charted depth'
      : `as little as ${formatDepth(danger.depthM, depthUnit)}`;
  } else if (danger.hazard === 'shallow') {
    // Named against the boat, not in the abstract. "0 m" means nothing on its
    // own; "less than your 2.2 m safety depth" is the sentence that decides
    // whether to turn.
    // `drval1` is the shallow LIMIT of the area, not a measured depth: the water
    // in it is at least this deep and may be no deeper. "As little as" is what
    // that means, and it is the reading to act on.
    const charted = danger.depthM === null
      ? 'no charted depth'
      : `as little as ${formatDepth(danger.depthM, depthUnit)}`;
    if (!boat) {
      detail = charted;
    } else {
      const safety = formatDepth(safetyDepth(boat), depthUnit);
      // Rounded to whole feet, 2.0 m and a 2.13 m safety depth both read "7 ft",
      // and "as little as 7 ft, less than your 7 ft safety depth" is nonsense on
      // its face. The comparison is <=, so this wording is true either way.
      const sameToTheEye = danger.depthM !== null
        && formatDepth(danger.depthM, depthUnit) === safety;
      detail = `${charted}, ${sameToTheEye ? 'no more than' : 'less than'} your ${safety} safety depth`;
    }
  } else {
    detail = danger.depthM === null
      ? 'least depth not surveyed'
      : formatDepth(danger.depthM, depthUnit);
  }

  return `${what} ${formatDistance(danger.distanceM, distanceUnit)} ahead — ${detail}`;
}
